/* eslint-disable */
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  SetStateAction,
} from "react";
import {
  makeStyles,
  Typography,
  CardContent,
  Card,
  CardActionArea,
  Tooltip,
  Popper,
  Grow,
  ClickAwayListener,
  MenuList,
  Paper,
  Grid,
  Button,
  Container,
  Icon,
} from "@material-ui/core";
import {
  green as myGreen,
  red as myRed,
  grey as myGrey,
  blue as myBlue,
} from "@material-ui/core/colors";
import {
  Person as PersonIcon,
  PersonOutline,
  PhoneInTalk as OutgoingIcon,
  PhoneCallback as IncomingIcon,
  RemoveCircle,
  PanTool,
  FiberManualRecord as Circle,
  ExpandMoreOutlined,
  DialpadSharp,
  StarSharp,
  AccessTime as ClockIcon,
} from "@material-ui/icons";
import NotificationsIcon from "@material-ui/icons/Notifications";
import hash from "object-hash";
import barge from "./icons/barge.png";
import callImg from "./icons/call.png";
import listen from "./icons/listen.png";
import message from "./icons/message.png";
import take from "./icons/Take.png";
import whisper from "./icons/whisper.png";
import { UserState } from "./QueueLeaderboard";
import { usePresenceTimer, TimerState } from "./GlobalHooks/usePresenceTimer";
import { TimerDisplay } from "./Profile/components/ExPresence";

interface PresenceTimerData {
  timerStart: number; // Epoch time when timed presence started
  timerEnd: number; // Epoch timed when timed presence is due to end.
  duration: number; // number of seconds for the presence timer.
  autoSwitchback: boolean; // Whether the timer is set to automatically switch back or will require manual switching.
  userDisplay: string; // The name of the presence prefixed by the category ie. AVL:Limited Available, or DND:In a Meeting.
}

const CACHE_KEY_PREFIX = "profileImg_";
const CACHE_EXPIRATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
const DEFAULT_IMAGE = "./default-user-image.ico"; // Fallback image
const DB_NAME = "ImageCacheDB";
const STORE_NAME = "images";

// Open or create the IndexedDB database
function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "id" });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

// Get a cached image from IndexedDB
async function getCachedImage(userId: string): Promise<string | null> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, "readonly");
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(CACHE_KEY_PREFIX + userId);

    request.onsuccess = () => {
      const cached = request.result;
      if (cached && Date.now() - cached.timestamp < CACHE_EXPIRATION) {
        resolve(cached.dataUrl);
      } else {
        resolve(null);
      }
    };

    request.onerror = () => reject(request.error);
  });
}

// Set a cached image in IndexedDB
async function setCachedImage(userId: string, dataUrl: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);
    const request = store.put({
      id: CACHE_KEY_PREFIX + userId,
      dataUrl,
      timestamp: Date.now(),
    });

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

// Fetch and cache the profile image
async function fetchProfileImage(url: string, userId: string): Promise<string> {
  const cachedImage = await getCachedImage(userId);
  if (cachedImage) return cachedImage;

  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP Error ${response.status}`);

    const blob = await response.blob();
    const reader = new FileReader();

    return new Promise<string>((resolve) => {
      reader.onloadend = async () => {
        const dataUrl = reader.result as string;
        await setCachedImage(userId, dataUrl);
        resolve(dataUrl);
      };
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.warn(`Failed to fetch profile image for ${userId}:`, error);
    await setCachedImage(userId, DEFAULT_IMAGE); // Cache the fallback image
    return DEFAULT_IMAGE;
  }
}

const green = {
  ...myGreen,
  1000: "rgba(144, 238, 144, 0.2)",
};

const red = {
  ...myRed,
  1000: "rgba(255, 204, 203, 0.2)",
};

const grey = {
  ...myGrey,
  1000: "rgba(211, 211, 211, 0.2)",
};

const blue = {
  ...myBlue,
  1000: "rgba(184, 230, 243, 0.3)",
};

// format duration for elapsedTime="full"
export const formatDuration = (time: number) => {
  if (time < 0) {
    return "00:00";
  }
  const durationSeconds = time;
  const durationMinutes = Math.floor(durationSeconds / 60);
  const durationHours =
    durationMinutes >= 60 ? Math.floor(durationMinutes / 60) : "";
  const seconds = durationSeconds % 60;
  const minutes = durationMinutes % 60;
  return durationHours !== ""
    ? `${durationHours.toString().padStart(2, "0")}:${minutes
        .toString()
        .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`
    : `${durationMinutes.toString().padStart(2, "0")}:${seconds
        .toString()
        .padStart(2, "0")}`;
};

// format duration for elapsedTime="approximate"
export const formatApproxDuration = (sec: number) => {
  if (sec < 0) {
    return "0s";
  }
  if (sec < 60) {
    return `${sec}s`;
  }
  if (sec < 3600) {
    return `${Math.floor(sec / 60)}m`;
  }
  if (sec < 86400) {
    return `${Math.floor(sec / 3600)}h`;
  }
  return `${Math.floor(sec / 86400)}d`;
};

// Calculate elapsed time for 24hr cycle
export const formatDailyDuration = (time: number) => {
  if (time < 0) {
    return "00:00";
  }

  const currentTime = new Date();
  const midnight = new Date(currentTime);
  // Reset to midnight
  midnight.setHours(0, 0, 0, 0);

  const elapsedFromMidnight = Math.floor((currentTime.getTime() - midnight.getTime()) / 1000);

  // Use smallest value between elapsed time and the provided time
  const durationSeconds = Math.min(elapsedFromMidnight, time);

  const hours = Math.floor(durationSeconds / 3600);
  const minutes = Math.floor((durationSeconds % 3600) / 60);
  const seconds = durationSeconds % 60;

  return hours > 0
    ? `${hours.toString().padStart(2, "0")}:${minutes
        .toString()
        .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`
    : `${minutes.toString().padStart(2, "0")}:${seconds
        .toString()
        .padStart(2, "0")}`;
}

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    fontFamily: "roboto",
    fontSize: "0.85rem",
    padding: 6,
    height: 85,
  },
  pos: {
    display: "flex",
    flexDirection: "column",
  },
  paper: {
    backgroundColor: "black",
  },
}));

const useDarkStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    fontFamily: "roboto",
    fontSize: "0.85rem",
    padding: 6,
    height: 85,
  },
  pos: {
    display: "flex",
    flexDirection: "column",
  },
  paper: {
    backgroundColor: "white",
  },
}));

const UserWidget = React.memo(
  ({
    timingFormat,
    user,
    userFormat,
    orgId,
    rcAccessToken,
    setDialer,
    isAdmin,
    isCurrentUser,
    showPortraits,
    browserPhone,
    hasPresenceTimer,
    elapsedTime,
    offset,
    mode,
    adminClick,
    clickOnMessage,
    clickOnPhone,
    clickOnHand,
    handRaised,
    showHandRaise,
    supervise,
    handlePopupToggle,
    popperOpen,
    origin,
    vipList,
    userState,
    late
  }: {
    timingFormat: string;
    user: {
      id: string;
      display?: string;
      lastName?: string;
      firstName?: string;
      department?: string;
      site?: {
        name: string;
      };
      extensionNumber?: string;
      lastDndStatus?: string;
      lastPresenceStatus?: string;
      messageTimestamp?: number;
      signedUrl?: string;
      status?: string;
      glipStatus?: string;
      type?: string;
      emergency?: boolean;
      queues?: Array<{
        queueId: string;
        name: string;
        acceptCurrentQueueCalls?: boolean;
      }>;
      state?: UserState;
      uri?: string;
      late?: boolean | undefined;
      timerInfo?: PresenceTimerData;
    };
    userFormat?: (user: any) => string;
    orgId: string | undefined;
    rcAccessToken: string;
    setDialer: React.Dispatch<SetStateAction<boolean>>;
    isAdmin: boolean;
    isCurrentUser: boolean;
    showPortraits: boolean;
    browserPhone: boolean;
    hasPresenceTimer: boolean;
    elapsedTime: string;
    offset: number;
    mode: string;
    adminClick: any;
    clickOnMessage: any;
    clickOnPhone: any;
    clickOnHand: any;
    handRaised: boolean;
    showHandRaise: boolean;
    supervise: boolean;
    handlePopupToggle: any;
    popperOpen: any;
    origin: string;
    vipList: any;
    userState: UserState | undefined;
    late: boolean | undefined;
  }) => {
    // Destructure user properties
    const {
      id,
      display,
      lastName,
      firstName,
      department,
      site,
      extensionNumber,
      lastDndStatus,
      lastPresenceStatus,
      messageTimestamp,
      signedUrl,
      status,
      glipStatus,
      type,
      emergency,
      queues,
      state,
      uri,
      late: userLate,
      timerInfo
    } = user;

    // Use userLate if provided, otherwise fall back to the late prop
    const finalLate = userLate !== undefined ? userLate : late;
    
    // messageTimestamp is already a number, so we can use it directly
    const messageTimestampNumber = messageTimestamp;

    const [image, setImage] = useState<string | null>(null);
    const [colorWeight, setColorWeight] = useState<600 | 300>(600);
    const [backgroundWeight, setBackgroundWeight] = useState<100 | 1000>(100);
    const [color, setColor] = useState<"initial" | "white">("initial");
    const [borderColors, setBorderColors] = useState<
      | { green: "green"; red: "red"; grey: "grey"; blue: "blue"; }
      | { green: "lightgreen"; red: "lightcoral"; grey: "lightgrey"; blue: "lightblue"; }
    >({ green: "green", red: "red", grey: "grey", blue: "blue" });
    const [duration, setDuration] = useState<number | undefined>();
    // const [callTimer, setCallTimer] = useState<Map<string, any> | undefined>();
    const [showTooltip, setShowTooltip] = useState<boolean>();
    const [openPopup, setOpenPopup] = useState<boolean>(false);
    const anchorRef = useRef<HTMLButtonElement>(null);
    const handleClose = useCallback((event: any) => {
      if (anchorRef.current && anchorRef.current.contains(event.target)) {
        return;
      }
      setOpenPopup(false);
    }, []);

    const [hasPartialAccess, setHasPartialAccess] = useState<boolean>(true);

    const departmentRef = useRef<HTMLDivElement>(null);
    const classes = mode === "light" ? useStyles() : useDarkStyles();

    // Use the centralized presence timer hook only for late timer
    const {
      overstayTime,
      formattedOverstayTime,
      timerState
    } = usePresenceTimer({
      presenceTimerData: timerInfo,
      late: finalLate,
      elapsedTime,
      offset,
      display
    });

    useEffect(() => {
      const handleResize = () => {
        setShowTooltip(
          departmentRef &&
            departmentRef.current?.offsetHeight !== undefined &&
            departmentRef.current?.scrollWidth !== undefined &&
            departmentRef.current?.offsetWidth !== undefined &&
            (departmentRef.current?.offsetHeight <
              departmentRef.current?.scrollHeight ||
              departmentRef.current?.offsetWidth <
                departmentRef.current?.scrollWidth)
        );
      };
      window.addEventListener("resize", handleResize);
      return () => {
        window.removeEventListener("resize", handleResize);
      };
    }, []);

    useEffect(() => {
      fetchProfileImage(uri + `?access_token=${rcAccessToken}`, id).then(
        setImage
      );
    }, [uri, id]);

    useEffect(() => {
      setShowTooltip(
        departmentRef &&
          departmentRef.current?.offsetHeight !== undefined &&
          departmentRef.current?.scrollWidth !== undefined &&
          departmentRef.current?.offsetWidth !== undefined &&
          (departmentRef.current?.offsetHeight <
            departmentRef.current?.scrollHeight ||
            departmentRef.current?.offsetWidth <
              departmentRef.current?.scrollWidth)
      );
    }, []);

    useEffect(() => {
      if (messageTimestampNumber === undefined || elapsedTime === "none") {
        return () => {};
      }
      // set timer for the presence
      if (duration === undefined) {
        setDuration(
          new Date().getTime() + offset - messageTimestampNumber
        );
        return () => {};
      }
      let cb: any;
      if (elapsedTime === "full") {
        cb = setTimeout(() => {
          setDuration(
            new Date().getTime() + offset - messageTimestampNumber
          );
        }, 3000);
      }

      if (elapsedTime === "daily") {
        cb = setTimeout(() => {
          setDuration(
            new Date().getTime() + offset - messageTimestampNumber
          );
        }, 3000);
      }

      if (elapsedTime === "approximate") {
        if (duration < 60000) {
          cb = setTimeout(() => {
            setDuration(
              new Date().getTime() +
                offset -
                messageTimestampNumber
            );
          }, new Date().getTime() - messageTimestampNumber + offset - duration + 1000);
        } else if (duration < 3600000) {
          cb = setTimeout(() => {
            setDuration(
              new Date().getTime() +
                offset -
                messageTimestampNumber
            );
          }, new Date().getTime() - messageTimestampNumber + offset - duration + 60000);
        } else if (duration < 86400000) {
          cb = setTimeout(() => {
            setDuration(
              new Date().getTime() +
                offset -
                messageTimestampNumber
            );
          }, new Date().getTime() - messageTimestampNumber + offset - duration + 3600000);
        } else {
          cb = setTimeout(() => {
            setDuration(
              new Date().getTime() +
                offset -
                messageTimestampNumber
            );
          }, new Date().getTime() - messageTimestampNumber + offset - duration + 86400000);
        }
      }

      return () => {
        clearTimeout(cb);
      };
    }, [offset, messageTimestampNumber, duration, elapsedTime]);

    useEffect(() => {
      if (mode === "light") {
        setColorWeight(600);
        setBackgroundWeight(100);
        setColor("initial");
        setBorderColors({ green: "green", red: "red", grey: "grey", blue: "blue" });
        return;
      }

      setColorWeight(300);
      setBackgroundWeight(1000);
      setColor("white");
      setBorderColors({
        green: "lightgreen",
        red: "lightcoral",
        grey: "lightgrey",
        blue: "lightblue",
      });
    }, [mode]);





    /* eslint-disable default-case */
    const handleParentPosts = useCallback(
      (e: React.KeyboardEvent | React.MouseEvent, eventType: string) => {
        const dialerRef = document.querySelector(
          "#ex-dialer"
        ) as HTMLIFrameElement;
        if (dialerRef) {
          switch (eventType) {
            case "Listen":
              e.stopPropagation();
              dialerRef.contentWindow!.postMessage(
                {
                  type: "rc-adapter-new-call",
                  phoneNumber: `*80 ${extensionNumber}`,
                  toCall: true,
                },
                "*"
              );
              handlePopupToggle();
              setDialer(true);
              break;

            case "Whisper":
              e.stopPropagation();
              dialerRef.contentWindow!.postMessage(
                {
                  type: "rc-adapter-new-call",
                  phoneNumber: `*81 ${extensionNumber}`,
                  toCall: true,
                },
                "*"
              );
              handlePopupToggle();
              setDialer(true);
              break;

            case "Barge":
              e.stopPropagation();
              dialerRef.contentWindow!.postMessage(
                {
                  type: "rc-adapter-new-call",
                  phoneNumber: `*82 ${extensionNumber}`,
                  toCall: true,
                },
                "*"
              );
              handlePopupToggle();
              setDialer(true);
              break;

            case "Take":
              e.stopPropagation();
              dialerRef.contentWindow!.postMessage(
                {
                  type: "rc-adapter-new-call",
                  phoneNumber: `*83 ${extensionNumber}`,
                  toCall: true,
                },
                "*"
              );
              handlePopupToggle();
              setDialer(true);
              break;

            case "Call":
              e.stopPropagation();
              clickOnPhone(extensionNumber);
              handlePopupToggle();
              setDialer(true);
              break;

            case "Message":
              e.stopPropagation();
              clickOnMessage(extensionNumber);
              handlePopupToggle();
              setDialer(true);
              break;

            default:
              e.stopPropagation();
              setOpenPopup(false);
              // setDialer(true)
              break;
          }
        }
      },
      []
    );

    function regexCheck(str: string, number: string): boolean {
      const escapeRegex = (s: string): string =>
        s.replace(/([.+^=!:${}()|\\])/g, "\\$1");

      const temp = "^".concat(
        number.split("*").map(escapeRegex).join(".*"),
        "$"
      );
      const r = new RegExp(temp.split("?").join("."));

      return r.test(str);
    }

    const cardContent = (
      <>
        {showPortraits && (
          <div
            className={classes.pos}
            style={{
              width: 50,
              alignItems: "flex-start",
            }}
          >
            <img
              aria-label="portrait"
              src={image || "./default-user-image.ico"}
              alt="Profile"
              style={{ borderRadius: "50%", height: 50, width: 50 }}
              onError={(e: any) => {
                e.target.src = "./default-user-image.ico";
              }}
            />
          </div>
        )}
        <CardContent
          style={{
            width: showPortraits ? "calc(100% - 50px)" : "100%",
            display: "flex",
            alignItems: "center",
            padding: 6,
          }}
        >
          <Typography
            className={classes.pos}
            component="div"
            style={{
              width: "250%",
              alignItems: "flex-start",
            }}
          >
            <div
              className="notranslate"
              style={{ fontWeight: "bold", fontSize: "0.85rem" }}
            >
              {userFormat ? userFormat(user) : (display || `${firstName || ""} ${lastName || ""}`.trim() || id)}
            </div>
            {status !== "<None>" && (
              <div className="notranslate" style={{ fontSize: "0.7rem" }}>
                {status}
              </div>
            )}
            <div
              style={{
                fontSize: "0.7rem",
                display: "flex",
                alignItems: "center",
              }}
            >
              {display === undefined && (
                <>
                  <Circle
                    style={{
                      color: "green",
                      fontSize: "0.8rem",
                      marginRight: 3,
                    }}
                    aria-label="Available icon"
                    aria-hidden={false}
                    role="img"
                  />
                  <span className="">Available</span>
                </>
              )}
              {display && display.startsWith("DND:") && (
                <>
                  <RemoveCircle
                    aria-hidden={false}
                    role="img"
                    aria-label="Do Not Disturb icon"
                    style={{ color: "red", fontSize: "0.7rem" }}
                  /> 
                  <span style={{ marginRight: 3 }} className="notranslate">
                    {(display || "").substring(4)}
                  </span>
                </>
              )}
              {display && display.startsWith("INV:") && !finalLate &&(
                <>
                  <Circle
                    style={{
                      color: "grey",
                      fontSize: "0.8rem",
                      marginRight: 3,
                    }}
                    aria-label="Invisible icon"
                    aria-hidden={false}
                    role="img"
                  />
                  <span style={{ marginRight: 3 }} className="notranslate">
                    {(display || "").substring(4)}
                  </span>
                </>
              )}
              {display && display.startsWith("BSY:") && !finalLate &&(
                <>
                  <Circle
                    style={{ color: "red", fontSize: "0.8rem", marginRight: 3 }}
                    aria-label="Busy icon"
                    aria-hidden={false}
                    role="img"
                  />
                  <span style={{ marginRight: 3 }} className="notranslate">
                    {(display || "").substring(4)}
                  </span>
                </>
              )}
              {display && display.startsWith("AVL:") && !finalLate &&(
                <>
                  <Circle
                    style={{
                      color: "green",
                      fontSize: "0.8rem",
                      marginRight: 3,
                    }}
                    aria-label="Available icon"
                    aria-hidden={false}
                    role="img"
                  />
                  <span style={{ marginRight: 3 }} className="notranslate">
                    {(display || "").substring(4)}
                  </span>
                </>
              )}

              {hasPresenceTimer &&
                elapsedTime === "full" &&
                messageTimestampNumber &&
                duration && (
                  <span style={{ fontWeight: "bold" }}>
                    {" "}
                    ({formatDuration(Math.round(duration / 1000))})
                    {finalLate && formattedOverstayTime && (
                      <>
                       
                        {" "}
                        <ClockIcon style={{ fontSize: "0.8rem", marginLeft: 2, marginRight: 2 }} />
                        {" "}
                        ({formattedOverstayTime})
                      </>
                    )}
                  </span>
                )}
              {hasPresenceTimer &&
                elapsedTime === "approximate" &&
                messageTimestampNumber &&
                duration && (
                  <span style={{ fontWeight: "bold" }}>
                    {" "}
                    ({formatApproxDuration(Math.round(duration / 1000))})
                    {finalLate && formattedOverstayTime && (
                      <>
                      
                        {" "}
                        <ClockIcon style={{ fontSize: "0.8rem", marginLeft: 2, marginRight: 2 }} />
                        {" "}
                        ({formattedOverstayTime})
                      </>
                    )}
                  </span>
                )}
              {hasPresenceTimer &&
                elapsedTime === "daily" &&
                messageTimestampNumber &&
                duration && (
                  <span style={{ fontWeight: "bold" }}>
                    {" "}
                    ({formatDailyDuration(Math.round(duration / 1000))})
                    {finalLate && formattedOverstayTime && (
                      <>

                        {" "}
                        <ClockIcon style={{ fontSize: "0.8rem", marginLeft: 2, marginRight: 2 }} />
                        {" "}
                        ({formattedOverstayTime})
                      </>
                    )}
                  </span>
                )}
            </div>
            <div
              key={`${id}#CallInfo`}
              style={{
                fontSize: "0.7rem",
                flexWrap: "wrap",
                display:
                  userState && 
                  userState.state !== "IDLE" &&
                  (userState.state === "RINGING" || lastPresenceStatus === "Busy")
                    ? "flex"
                    : "none"
              }}
            >
              {queues &&
                userState &&
                userState.state === "ON QUEUE CALL" &&
                userState.queueId && (
                  <Tooltip
                    title={`On Queue Call - ${(
                      <span className="notranslate">
                        {queues.find(
                          (queue) => queue.queueId === userState?.queueId
                        )?.name || ""}
                      </span>
                    )}`}
                  >
                    <PersonIcon
                      aria-hidden={false}
                      role="img"
                      style={{
                        color: red[colorWeight],
                        width: 13,
                        height: 13,
                        alignSelf: "center",
                      }}
                    />
                  </Tooltip>
                )}
              {userState?.state !== "RINGING" &&
                (userState &&
                (userState.direction === "Inbound" ||
                  (userState.direction === "Internal" &&
                    userState.to?.extensionId === id)) ? (
                  <IncomingIcon
                    style={{ fontSize: "0.85rem", float: "left" }}
                  />
                ) : (
                  <OutgoingIcon
                    style={{ fontSize: "0.85rem", float: "left" }}
                  />
                ))}
              {userState?.state === "RINGING" && (
                <span className="ringing-bell">
                  <NotificationsIcon style={{ fontSize: "0.85rem" }} />
                </span>
              )}
              <p
                className="caller-name-number notranslate"
                style={{
                  margin: 0,
                }}
              >
                {userState?.state === "RINGING" ? "Ringing " : ""}
                {userState &&
                (userState.direction === "Inbound" ||
                  (userState.direction === "Internal" &&
                    userState.to?.extensionId === id))
                  ? userState.from?.name
                    ? userState.from?.name + " "
                    : ""
                  : userState?.to?.name
                  ? userState.to.name + " "
                  : ""}
                {userState &&
                (userState.direction === "Inbound" ||
                  (userState.direction === "Internal" &&
                    userState.to?.extensionId === id))
                  ? userState?.from?.phoneNumber || ""
                  : userState?.to?.phoneNumber || ""}
              </p>
              {hasPresenceTimer && userState && userState.time && (
                <>
                  {userState.direction === "Inbound" ||
                  (userState.direction === "Internal" &&
                    userState.to?.extensionId === id) ? (
                    <>
                      <span
                        className="notranslate"
                        style={{
                          fontWeight: "bold",
                          display: "flex",
                          alignItems: "center",
                          whiteSpace: "nowrap",
                          textOverflow: "clip",
                          gap: 2,
                          paddingRight: 4,
                        }}
                      >
                        {vipList.map((vip: any) => {
                          if (
                            regexCheck(
                              userState?.from?.phoneNumber || "",
                              vip?.number
                            )
                          ) {
                            return (
                              <div
                                className="notranslate"
                                style={{
                                  fontSize: "0.7rem",
                                  display: "flex",
                                  gap: 2,
                                  alignItems: "center",
                                }}
                              >
                                <StarSharp
                                  style={{
                                    color:
                                      mode === "light" ? "#dc9d00" : "#DAA520",
                                    height: "12px",
                                    width: "12px",
                                  }}
                                />
                                <Typography
                                  className="notranslate"
                                  style={{
                                    fontSize: "0.7rem",
                                    color:
                                      mode === "light" ? "#dc9d00" : "#DAA520",
                                    whiteSpace: "nowrap",
                                    textOverflow: "clip",
                                  }}
                                >
                                  {vip.name}
                                </Typography>
                              </div>
                            );
                          }
                          return null;
                        })}
                      </span>
                      <Typography
                        style={{
                          fontSize: "0.7rem",
                          whiteSpace: "nowrap",
                          textOverflow: "clip",
                        }}
                      >
                        {" for "}
                        {formatDuration(
                          Math.round(
                            (new Date().getTime() -
                              new Date(userState.time).getTime()) /
                              1000
                          )
                        )}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <span
                        className="notranslate"
                        style={{
                          fontWeight: "bold",
                          display: "flex",
                          alignItems: "center",
                          whiteSpace: "nowrap",
                          textOverflow: "clip",
                          gap: 2,
                          paddingRight: 4,
                        }}
                      >
                        {vipList.map((vip: any) => {
                          if (
                            regexCheck(
                              userState?.to?.phoneNumber || "",
                              vip?.number
                            )
                          ) {
                            return (
                              <div
                                className="notranslate"
                                style={{
                                  fontSize: "0.7rem",
                                  display: "flex",
                                  gap: 2,
                                  alignItems: "center",
                                }}
                              >
                                <StarSharp
                                  style={{
                                    color:
                                      mode === "light" ? "#dc9d00" : "#DAA520",
                                    height: "12px",
                                    width: "12px",
                                  }}
                                />
                                <Typography
                                  className="notranslate"
                                  style={{
                                    fontSize: "0.7rem",
                                    color:
                                      mode === "light" ? "#dc9d00" : "#DAA520",
                                    whiteSpace: "nowrap",
                                    textOverflow: "clip",
                                  }}
                                >
                                  {vip.name}
                                </Typography>
                              </div>
                            );
                          }
                          return null;
                        })}
                      </span>
                      <Typography
                        className="notranslate"
                        style={{
                          fontSize: "0.7rem",
                          whiteSpace: "nowrap",
                          textOverflow: "clip",
                        }}
                      >
                        {" for "}
                        {formatDuration(
                          Math.round(
                            (new Date().getTime() -
                              new Date(userState.time).getTime()) /
                              1000
                          )
                        )}
                      </Typography>
                    </>
                  )}
                </>
              )}
            </div>

            {/* {calls.map((call) => (
            <div key={call.id}>
              {call.inbound && (
                <div
                  style={{
                    fontSize: "0.7rem",
                    display: "flex",
                    flexWrap: "wrap",
                  }}
                >
                  {queues &&
                    userState &&
                    userState.state === "ON QUEUE CALL" &&
                    userState.queueId && (
                      <Tooltip
                        title={`On Queue Call - ${queues.find(
                          (queue) => queue.queueId === userState?.queueId
                        )?.name || ""
                          }`}
                      >
                        <PersonIcon
                          aria-hidden={false}
                          role="img"
                          style={{
                            color: red[colorWeight],
                            width: 13,
                            height: 13,
                            alignSelf: "center",
                          }}
                        />
                      </Tooltip>
                    )}
                  <IncomingIcon
                    style={{ fontSize: "0.85rem", float: "left" }}
                  />
                  <p
                    className="caller-name-number"
                    style={{
                      margin: 0,
                    }}
                  >
                    {" "}
                    {call.fromName || ""} ({call.fromNumber || ""}
                  </p>
                  {hasPresenceTimer && call.duration && (
                    <>
                      <span
                        style={{
                          fontWeight: "bold",
                          display: "flex",
                          alignItems: "center",
                          whiteSpace: "nowrap",
                          textOverflow: "clip",
                          gap: 2,
                          paddingRight: 4,
                        }}
                      >
                        {vipList.map((vip: any) => {
                          if (regexCheck(call?.fromNumber || "", vip?.number)) {
                            return (
                              <div
                                style={{
                                  fontSize: "0.7rem",
                                  display: "flex",
                                  gap: 2,
                                  alignItems: "center",
                                }}
                              >
                                <StarSharp
                                  style={{
                                    color:
                                      mode === "light" ? "#dc9d00" : "#DAA520",
                                    height: "12px",
                                    width: "12px",
                                  }}
                                />
                                <Typography
                                  style={{
                                    fontSize: "0.7rem",
                                    color:
                                      mode === "light" ? "#dc9d00" : "#DAA520",
                                    whiteSpace: "nowrap",
                                    textOverflow: "clip",
                                  }}
                                >
                                  {vip.name}
                                </Typography>
                              </div>
                            );
                          }
                          return null;
                        })}
                      </span>
                      <Typography
                        style={{
                          fontSize: "0.7rem",
                          whiteSpace: "nowrap",
                          textOverflow: "clip",
                        }}
                      >
                        {" for "}
                        {formatDuration(Math.round(call.duration / 1000))}
                      </Typography>
                    </>
                  )}
                  )
                </div>
              )}
              {call.outbound && (
                <div
                  style={{
                    fontSize: "0.7rem",
                    display: "flex",
                    flexWrap: "wrap",
                  }}
                >
                  {queues &&
                    userState &&
                    userState.state === "ON QUEUE CALL" &&
                    userState.queueId && (
                      <Tooltip
                        title={`On Queue Call - ${queues.find(
                          (queue) => queue.queueId === userState?.queueId
                        )?.name || ""
                          }`}
                      >
                        <PersonIcon
                          aria-hidden={false}
                          role="img"
                          style={{
                            color: red[colorWeight],
                            width: 13,
                            height: 13,
                            alignSelf: "center",
                          }}
                        />
                      </Tooltip>
                    )}
                  <OutgoingIcon
                    style={{ fontSize: "0.85rem", float: "left" }}
                  />
                  <p
                    className="caller-name-number"
                    style={{
                      margin: 0,
                    }}
                  >
                    {call.toName || ""} ({call.toNumber || ""}
                  </p>
                  {hasPresenceTimer && call.duration && (
                    <>
                      <span
                        style={{
                          fontWeight: "bold",
                          display: "flex",
                          alignItems: "center",
                          whiteSpace: "nowrap",
                          textOverflow: "clip",
                          gap: 2,
                          paddingRight: 4,
                        }}
                      >
                        {vipList.map((vip: any) => {
                          if (regexCheck(call?.toNumber || "", vip?.number)) {
                            return (
                              <div
                                style={{
                                  fontSize: "0.7rem",
                                  display: "flex",
                                  gap: 2,
                                  alignItems: "center",
                                }}
                              >
                                <StarSharp
                                  style={{
                                    color:
                                      mode === "light" ? "#dc9d00" : "#DAA520",
                                    height: "12px",
                                    width: "12px",
                                  }}
                                />
                                <Typography
                                  style={{
                                    fontSize: "0.7rem",
                                    color:
                                      mode === "light" ? "#dc9d00" : "#DAA520",
                                    whiteSpace: "nowrap",
                                    textOverflow: "clip",
                                  }}
                                >
                                  {vip.name}
                                </Typography>
                              </div>
                            );
                          }
                          return null;
                        })}
                      </span>
                      <Typography
                        style={{
                          fontSize: "0.7rem",
                          whiteSpace: "nowrap",
                          textOverflow: "clip",
                        }}
                      >
                        {" for "}
                        {formatDuration(Math.round(call.duration / 1000))}
                      </Typography>
                    </>
                  )}
                  )
                </div>
              )}
            </div>
          ))} */}
          </Typography>
          <Typography
            className={classes.pos}
            style={{
              width: "30%",
              alignItems: "flex-end",
            }}
            component="div"
          >
            <div style={{ display: "flex" }}>
              {showHandRaise && (handRaised || isCurrentUser) && (
                <div
                  role="button"
                  aria-hidden="true"
                  tabIndex={0}
                  style={{
                    color: handRaised ? red[500] : "black",
                    padding: 0,
                  }}
                  className="MuiButtonBase-root MuiIconButton-root"
                  onClick={(event) => {
                    event.stopPropagation();
                    clickOnHand(id, handRaised, finalLate);
                  }}
                >
                  <span className="MuiIconButton-label">
                    <Tooltip title={handRaised ? "Lower Hand" : "Raise Hand"}>
                      <PanTool
                        className={
                          handRaised ? "hand-icon-active" : "hand-icon"
                        }
                        style={{ fontSize: "0.85rem" }}
                      />
                    </Tooltip>
                  </span>
                </div>
              )}
              {/* {browserPhone && extensionNumber !== undefined && (
              <div
                role="button"
                aria-hidden="true"
                tabIndex={0}
                style={{
                  color: mode === "dark" ? "white" : "black",
                  padding: 0,
                  marginLeft: 5,
                }}
                className="MuiButtonBase-root MuiIconButton-root"
                onClick={(event) => {
                  event.stopPropagation();
                  clickOnMessage(extensionNumber);
                }}
              >
                <span className="MuiIconButton-label">
                  <ChatBubble style={{ fontSize: "0.85rem" }} />
                </span>
              </div>
            )} */}
              {extensionNumber !== undefined && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                  }}
                >
                  <div
                    aria-hidden="true"
                    tabIndex={0}
                    style={{
                      color: mode === "dark" ? "white" : "black",
                      padding: 0,
                      marginLeft: 1,
                      width: "1em",
                    }}
                    onClick={(event) => {
                      event.stopPropagation();
                      handlePopupToggle();
                    }}
                  >
                    <button
                      ref={anchorRef}
                      className="more-button"
                      style={{
                        width: "2em",
                        height: "2em",
                        padding: 0,
                        margin: 0,
                        color: mode === "light" ? "black" : "white",
                        cursor: "pointer",
                      }}
                    >
                      <span>
                        <DialpadSharp style={{ fontSize: "1rem" }} />
                      </span>
                    </button>
                  </div>
                  <Popper
                    id={
                      "popper " + mode === "light"
                        ? " light-popper"
                        : "dark-popper"
                    }
                    open={popperOpen}
                    anchorEl={anchorRef.current}
                    role={undefined}
                    placement="bottom"
                    transition
                    disablePortal
                    style={{ borderRadius: 100, zIndex: 100 }}
                  >
                    {({ TransitionProps, placement }) => (
                      <Grow
                        // eslint-disable-next-line react/jsx-props-no-spreading
                        {...TransitionProps}
                        style={{
                          transformOrigin:
                            placement === "top" ? "bottom" : "top",
                        }}
                      >
                        <Paper
                          className={classes.pos}
                          style={{
                            borderRadius: 8,
                            boxShadow:
                              "0px 2px 24px #00000066, 0px 2px 4px #00000044",
                          }}
                        >
                          <ClickAwayListener
                            onClickAway={() => handlePopupToggle(id)}
                          >
                            <MenuList
                              // eslint-disable-next-line react/jsx-no-bind
                              autoFocusItem={popperOpen}
                              id="composition-menu"
                              aria-labelledby="composition-button"
                              // eslint-disable-next-line react/jsx-no-bind
                              style={{ paddingBottom: 0 }}
                            >
                              <div
                                className="dropdown-container"
                                style={{
                                  background:
                                    mode === "light"
                                      ? "#eee"
                                      : "linear-gradient(1deg,#111111,#222)",
                                  border:
                                    mode === "light"
                                      ? "1px solid #97c5da"
                                      : "2px solid #111",
                                }}
                              >
                                <Container disableGutters={true}>
                                  <Grid
                                    container
                                    spacing={1}
                                    style={{ padding: 5, maxWidth: 300 }}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {/* Canadian Nurses only want Listen, Text and Call options. 1779013024 */}
                                    {
                                        // Canadian Nurses 👇
                                        orgId === "1779013024" && (
                                          
                                        // US LAB 👇
                                        //  orgId === "3154756020" && (
                                        <>
                                        {/* Listen */}
                                        <Grid
                                          item
                                          xs={4}
                                          style={{
                                            textAlign: "center",
                                            cursor: "default",
                                          }}
                                        >
                                          <div className="paper-centering">
                                            <div>
                                              <Paper
                                                className="paper-container"
                                                style={{
                                                  backgroundColor:
                                                    mode === "light"
                                                      ? "#eee"
                                                      : "#282828 !important",
                                                  border:
                                                    mode === "light"
                                                      ? "1px solid #97c5da"
                                                      : "1px solid #888",
                                                }}
                                              >
                                                <Button
                                                  onClick={(e) =>
                                                    e.stopPropagation()
                                                  }
                                                  className={`dropdown-button`}
                                                  style={{
                                                    backgroundColor:
                                                      mode === "light"
                                                        ? "#eee "
                                                        : "#282828 !important",
                                                  }}
                                                  fullWidth
                                                >
                                                  <img
                                                    alt="listen"
                                                    className="button-img"
                                                    src={listen}
                                                    style={{
                                                      filter:
                                                        mode === "light"
                                                          ? "invert(0)"
                                                          : "invert(1)",
                                                    }}
                                                    onClick={(e) =>
                                                      handleParentPosts(
                                                        e,
                                                        "Listen"
                                                      )
                                                    }
                                                  />
                                                </Button>
                                              </Paper>
                                              <Typography
                                                style={{
                                                  color:
                                                    mode === "light"
                                                      ? "black"
                                                      : "white",
                                                }}
                                              >
                                                Listen
                                              </Typography>
                                            </div>
                                          </div>
                                        </Grid>
                                        {/* Text */}
                                        <Grid
                                          item
                                          xs={4}
                                          style={{
                                            textAlign: "center",
                                            cursor: "default",
                                          }}
                                        >
                                          <div className="paper-centering">
                                            <div>
                                              <Paper
                                                className="paper-container"
                                                style={{
                                                  backgroundColor:
                                                    mode === "light"
                                                      ? "#eee"
                                                      : "#282828 !important",
                                                  border:
                                                    mode === "light"
                                                      ? "1px solid #97c5da"
                                                      : "1px solid #888",
                                                }}
                                              >
                                                {
                                                  // browserPhone &&
                                                  extensionNumber !== undefined ? (
                                                    <Button
                                                      fullWidth
                                                      className="dropdown-button"
                                                      onClick={(e) =>
                                                        e.stopPropagation()
                                                      }
                                                      style={{
                                                        backgroundColor:
                                                          mode === "light"
                                                            ? "#eee "
                                                            : "#282828 !important",
                                                      }}
                                                    >
                                                      <img
                                                        alt="text"
                                                        className="button-img"
                                                        src={message}
                                                        onClick={(e) =>
                                                          handleParentPosts(
                                                            e,
                                                            "Message"
                                                          )
                                                        }
                                                        style={{
                                                          filter:
                                                            mode === "light"
                                                              ? "invert(0)"
                                                              : "invert(1)",
                                                        }}
                                                      />
                                                    </Button>
                                                  ) : (
                                                    <Button
                                                      onClick={(e) =>
                                                        e.stopPropagation()
                                                      }
                                                      fullWidth
                                                      className="dropdown-button disabled-button"
                                                      style={{
                                                        backgroundColor:
                                                          mode === "light"
                                                            ? "#eee "
                                                            : "#282828 !important",
                                                      }}
                                                      disabled
                                                    >
                                                      <img
                                                        alt="text"
                                                        className="button-img disabled-button-img"
                                                        src={message}
                                                        style={{
                                                          filter:
                                                            mode === "light"
                                                              ? "invert(0)"
                                                              : "invert(1)",
                                                        }}
                                                      />
                                                    </Button>
                                                  )
                                                }
                                              </Paper>
                                              <Typography
                                                style={{
                                                  color:
                                                    mode === "light"
                                                      ? "black"
                                                      : "white",
                                                }}
                                              >
                                                Text
                                              </Typography>
                                            </div>
                                          </div>
                                        </Grid>
                                        {/* Call */}
                                        <Grid
                                          item
                                          xs={4}
                                          style={{
                                            textAlign: "center",
                                            cursor: "default",
                                          }}
                                        >
                                          <div className="paper-centering">
                                            <div>
                                              <Paper
                                                className="paper-container"
                                                style={{
                                                  backgroundColor:
                                                    mode === "light"
                                                      ? "#eee"
                                                      : "#282828 !important",
                                                  border:
                                                    mode === "light"
                                                      ? "1px solid #97c5da"
                                                      : "1px solid #888",
                                                }}
                                              >
                                                {
                                                  // browserPhone &&
                                                  extensionNumber !== undefined ? (
                                                    <Button
                                                      onClick={(e) =>
                                                        e.stopPropagation()
                                                      }
                                                      fullWidth
                                                      className="dropdown-button"
                                                      style={{
                                                        backgroundColor:
                                                          mode === "light"
                                                            ? "#eee "
                                                            : "#282828 !important",
                                                      }}
                                                    >
                                                      <img
                                                        alt="call"
                                                        className="button-img"
                                                        src={callImg}
                                                        style={{
                                                          filter:
                                                            mode === "light"
                                                              ? "invert(0)"
                                                              : "invert(1)",
                                                        }}
                                                        onClick={(e) =>
                                                          handleParentPosts(
                                                            e,
                                                            "Call"
                                                          )
                                                        }
                                                      />
                                                    </Button>
                                                  ) : (
                                                    <Button
                                                      onClick={(e) =>
                                                        e.stopPropagation()
                                                      }
                                                      fullWidth
                                                      className="dropdown-button disabled-button"
                                                      style={{
                                                        backgroundColor:
                                                          mode === "light"
                                                            ? "#eee "
                                                            : "#282828 !important",
                                                        border:
                                                          mode === "light"
                                                            ? "1px solid #97c5da"
                                                            : "1px solid #888",
                                                      }}
                                                      disabled
                                                    >
                                                      <img
                                                        alt="call"
                                                        className="button-img disabled-button-img"
                                                        src={callImg}
                                                        style={{
                                                          filter:
                                                            mode === "light"
                                                              ? "invert(0)"
                                                              : "invert(1)",
                                                        }}
                                                      />
                                                    </Button>
                                                  )
                                                }
                                              </Paper>
                                              <Typography
                                                style={{
                                                  color:
                                                    mode === "light"
                                                      ? "black"
                                                      : "white",
                                                }}
                                              >
                                                Call
                                              </Typography>
                                            </div>
                                          </div>
                                        </Grid>
                                        </>
                                      )
                                    }
                                    {
                                      // Canaqdian Nurses 👇
                                      orgId !== "1779013024" && (
                                        <>
                                        {supervise && (
                                          <>
                                            {/* Barge */}
                                            <Grid
                                              item
                                              xs={4}
                                              style={{
                                                textAlign: "center",
                                                cursor: "default",
                                              }}
                                            >
                                              <div className="paper-centering">
                                                <div>
                                                  <Paper
                                                    className={"paper-container"}
                                                    style={{
                                                      border:
                                                        mode === "light"
                                                          ? "1px solid #97c5da"
                                                          : "1px solid #888",
                                                      backgroundColor:
                                                        mode === "light"
                                                          ? "#eee"
                                                          : "#282828 !important",
                                                    }}
                                                  >
                                                    <Button
                                                      className={`dropdown-button`}
                                                      style={{
                                                        backgroundColor:
                                                          mode === "light"
                                                            ? "#eee "
                                                            : "#282828 !important",
                                                      }}
                                                      fullWidth
                                                      onClick={(e) =>
                                                        e.stopPropagation()
                                                      }
                                                    >
                                                      <img
                                                        alt="barge"
                                                        className="button-img"
                                                        style={{
                                                          filter:
                                                            mode === "light"
                                                              ? "invert(1)"
                                                              : "invert(0)",
                                                        }}
                                                        src={barge}
                                                        onClick={(e) =>
                                                          handleParentPosts(
                                                            e,
                                                            "Barge"
                                                          )
                                                        }
                                                      />
                                                    </Button>
                                                  </Paper>
                                                  <Typography
                                                    style={{
                                                      color:
                                                        mode === "light"
                                                          ? "black"
                                                          : "white",
                                                    }}
                                                  >
                                                    Barge
                                                  </Typography>
                                                </div>
                                              </div>
                                            </Grid>
                                            {/* Whisper */}
                                            <Grid
                                              item
                                              xs={4}
                                              style={{
                                                textAlign: "center",
                                                cursor: "default",
                                              }}
                                            >
                                              <div className="paper-centering">
                                                <div>
                                                  <Paper
                                                    className="paper-container"
                                                    style={{
                                                      backgroundColor:
                                                        mode === "light"
                                                          ? "#eee"
                                                          : "#282828 !important",
                                                      border:
                                                        mode === "light"
                                                          ? "1px solid #97c5da"
                                                          : "1px solid #888",
                                                    }}
                                                  >
                                                    <Button
                                                      className={`dropdown-button`}
                                                      style={{
                                                        backgroundColor:
                                                          mode === "light"
                                                            ? "#eee "
                                                            : "#282828 !important",
                                                      }}
                                                      fullWidth
                                                      onClick={(e) =>
                                                        e.stopPropagation()
                                                      }
                                                    >
                                                      <img
                                                        alt="whisper"
                                                        className="button-img"
                                                        style={{
                                                          filter:
                                                            mode === "light"
                                                              ? "invert(1)"
                                                              : "invert(0)",
                                                        }}
                                                        src={whisper}
                                                        onClick={(e) =>
                                                          handleParentPosts(
                                                            e,
                                                            "Whisper"
                                                          )
                                                        }
                                                      />
                                                    </Button>
                                                  </Paper>
                                                  <Typography
                                                    style={{
                                                      color:
                                                        mode === "light"
                                                          ? "black"
                                                          : "white",
                                                    }}
                                                  >
                                                    Whisper
                                                  </Typography>
                                                </div>
                                              </div>
                                            </Grid>
                                            {/* Take */}
                                            <Grid
                                              item
                                              xs={4}
                                              style={{
                                                textAlign: "center",
                                                cursor: "default",
                                              }}
                                              onClick={(e) =>
                                                handleParentPosts(e, "Take")
                                              }
                                            >
                                              <div className="paper-centering">
                                                <div>
                                                  <Paper
                                                    className="paper-container"
                                                    style={{
                                                      backgroundColor:
                                                        mode === "light"
                                                          ? "#eee"
                                                          : "#282828 !important",
                                                      border:
                                                        mode === "light"
                                                          ? "1px solid #97c5da"
                                                          : "1px solid #888",
                                                    }}
                                                  >
                                                    <Button
                                                      className={`dropdown-button`}
                                                      style={{
                                                        backgroundColor:
                                                          mode === "light"
                                                            ? "#eee "
                                                            : "#282828 !important",
                                                      }}
                                                      fullWidth
                                                      onClick={(e) =>
                                                        e.stopPropagation()
                                                      }
                                                    >
                                                      <img
                                                        alt="Take"
                                                        className="button-img"
                                                        style={{
                                                          filter:
                                                            mode === "light"
                                                              ? "invert(1)"
                                                              : "invert(0)",
                                                        }}
                                                        src={take}
                                                        onClick={(e) =>
                                                          handleParentPosts(
                                                            e,
                                                            "Take"
                                                          )
                                                        }
                                                      />
                                                    </Button>
                                                  </Paper>
                                                  <Typography
                                                    style={{
                                                      color:
                                                        mode === "light"
                                                          ? "black"
                                                          : "white",
                                                    }}
                                                  >
                                                    Take
                                                  </Typography>
                                                </div>
                                              </div>
                                            </Grid>
                                          </>
                                        )}
                                        {(supervise) && (
                                        // Listen
                                        <Grid
                                          item
                                          xs={4}
                                          style={{
                                            textAlign: "center",
                                            cursor: "default",
                                          }}
                                        >
                                          <div className="paper-centering">
                                            <div>
                                              <Paper
                                                className="paper-container"
                                                style={{
                                                  backgroundColor:
                                                    mode === "light"
                                                      ? "#eee"
                                                      : "#282828 !important",
                                                  border:
                                                    mode === "light"
                                                      ? "1px solid #97c5da"
                                                      : "1px solid #888",
                                                }}
                                              >
                                                <Button
                                                  onClick={(e) =>
                                                    e.stopPropagation()
                                                  }
                                                  className={`dropdown-button`}
                                                  style={{
                                                    backgroundColor:
                                                      mode === "light"
                                                        ? "#eee "
                                                        : "#282828 !important",
                                                  }}
                                                  fullWidth
                                                >
                                                  <img
                                                    alt="listen"
                                                    className="button-img"
                                                    src={listen}
                                                    style={{
                                                      filter:
                                                        mode === "light"
                                                          ? "invert(0)"
                                                          : "invert(1)",
                                                    }}
                                                    onClick={(e) =>
                                                      handleParentPosts(
                                                        e,
                                                        "Listen"
                                                      )
                                                    }
                                                  />
                                                </Button>
                                              </Paper>
                                              <Typography
                                                style={{
                                                  color:
                                                    mode === "light"
                                                      ? "black"
                                                      : "white",
                                                }}
                                              >
                                                Listen
                                              </Typography>
                                            </div>
                                          </div>
                                        </Grid>
                                        )}
                                        {/* Text */}
                                        <Grid
                                          item
                                          xs={supervise ? 4 : 6}
                                          style={{
                                            textAlign: "center",
                                            cursor: "default",
                                          }}
                                        >
                                          <div className="paper-centering">
                                            <div>
                                              <Paper
                                                className="paper-container"
                                                style={{
                                                  backgroundColor:
                                                    mode === "light"
                                                      ? "#eee"
                                                      : "#282828 !important",
                                                  border:
                                                    mode === "light"
                                                      ? "1px solid #97c5da"
                                                      : "1px solid #888",
                                                }}
                                              >
                                                {
                                                  // browserPhone &&
                                                  extensionNumber !== undefined ? (
                                                    <Button
                                                      fullWidth
                                                      className="dropdown-button"
                                                      onClick={(e) =>
                                                        e.stopPropagation()
                                                      }
                                                      style={{
                                                        backgroundColor:
                                                          mode === "light"
                                                            ? "#eee "
                                                            : "#282828 !important",
                                                      }}
                                                    >
                                                      <img
                                                        alt="text"
                                                        className="button-img"
                                                        src={message}
                                                        onClick={(e) =>
                                                          handleParentPosts(
                                                            e,
                                                            "Message"
                                                          )
                                                        }
                                                        style={{
                                                          filter:
                                                            mode === "light"
                                                              ? "invert(0)"
                                                              : "invert(1)",
                                                        }}
                                                      />
                                                    </Button>
                                                  ) : (
                                                    <Button
                                                      onClick={(e) =>
                                                        e.stopPropagation()
                                                      }
                                                      fullWidth
                                                      className="dropdown-button disabled-button"
                                                      style={{
                                                        backgroundColor:
                                                          mode === "light"
                                                            ? "#eee "
                                                            : "#282828 !important",
                                                      }}
                                                      disabled
                                                    >
                                                      <img
                                                        alt="text"
                                                        className="button-img disabled-button-img"
                                                        src={message}
                                                        style={{
                                                          filter:
                                                            mode === "light"
                                                              ? "invert(0)"
                                                              : "invert(1)",
                                                        }}
                                                      />
                                                    </Button>
                                                  )
                                                }
                                              </Paper>
                                              <Typography
                                                style={{
                                                  color:
                                                    mode === "light"
                                                      ? "black"
                                                      : "white",
                                                }}
                                              >
                                                Text
                                              </Typography>
                                            </div>
                                          </div>
                                        </Grid>
                                        {/* Call */}
                                        <Grid
                                          item
                                          xs={supervise ? 4 : 6}
                                          style={{
                                            textAlign: "center",
                                            cursor: "default",
                                          }}
                                        >
                                          <div className="paper-centering">
                                            <div>
                                              <Paper
                                                className="paper-container"
                                                style={{
                                                  backgroundColor:
                                                    mode === "light"
                                                      ? "#eee"
                                                      : "#282828 !important",
                                                  border:
                                                    mode === "light"
                                                      ? "1px solid #97c5da"
                                                      : "1px solid #888",
                                                }}
                                              >
                                                {
                                                  // browserPhone &&
                                                  extensionNumber !== undefined ? (
                                                    <Button
                                                      onClick={(e) =>
                                                        e.stopPropagation()
                                                      }
                                                      fullWidth
                                                      className="dropdown-button"
                                                      style={{
                                                        backgroundColor:
                                                          mode === "light"
                                                            ? "#eee "
                                                            : "#282828 !important",
                                                      }}
                                                    >
                                                      <img
                                                        alt="call"
                                                        className="button-img"
                                                        src={callImg}
                                                        style={{
                                                          filter:
                                                            mode === "light"
                                                              ? "invert(0)"
                                                              : "invert(1)",
                                                        }}
                                                        onClick={(e) =>
                                                          handleParentPosts(
                                                            e,
                                                            "Call"
                                                          )
                                                        }
                                                      />
                                                    </Button>
                                                  ) : (
                                                    <Button
                                                      onClick={(e) =>
                                                        e.stopPropagation()
                                                      }
                                                      fullWidth
                                                      className="dropdown-button disabled-button"
                                                      style={{
                                                        backgroundColor:
                                                          mode === "light"
                                                            ? "#eee "
                                                            : "#282828 !important",
                                                        border:
                                                          mode === "light"
                                                            ? "1px solid #97c5da"
                                                            : "1px solid #888",
                                                      }}
                                                      disabled
                                                    >
                                                      <img
                                                        alt="call"
                                                        className="button-img disabled-button-img"
                                                        src={callImg}
                                                        style={{
                                                          filter:
                                                            mode === "light"
                                                              ? "invert(0)"
                                                              : "invert(1)",
                                                        }}
                                                      />
                                                    </Button>
                                                  )
                                                }
                                              </Paper>
                                              <Typography
                                                style={{
                                                  color:
                                                    mode === "light"
                                                      ? "black"
                                                      : "white",
                                                }}
                                              >
                                                Call
                                              </Typography>
                                            </div>
                                          </div>
                                        </Grid>
                                        </>
                                      )
                                    }

                                  </Grid>
                                </Container>
                              </div>
                            </MenuList>
                          </ClickAwayListener>
                        </Paper>
                      </Grow>
                    )}
                  </Popper>
                </div>
              )}
            </div>

            <Tooltip
              disableHoverListener={!showTooltip}
              title={String(department || "")}
              placement="top"
              className="notranslate"
            >
              <div
                ref={departmentRef}
                style={{
                  display: "-webkit-box",
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: "vertical",
                  maxWidth: "100%",
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  fontSize: "0.7rem",
                }}
                className="notranslate"
              >
                {department || ""}
              </div>
            </Tooltip>
            <div style={{ fontSize: "0.7rem", display: "flex" }}>
              {(() => {
                if (queues === undefined) {
                  return (
                    <PersonIcon
                      aria-label="Busy but accepting queue calls"
                      aria-hidden={false}
                      role="img"
                      style={{ color: "#00000000" }}
                    />
                  );
                }

                if (lastDndStatus !== "TakeAllCalls" && queues.length) {
                  return (
                    <Tooltip
                      title={queues.map((queue) => (
                        <div className="notranslate" key={queue.queueId}>
                          {queue.name}
                        </div>
                      ))}
                      placeholder="Busy"
                    >
                      <PersonOutline
                        aria-label="Not accepting queue calls"
                        aria-hidden={false}
                        role="img"
                        style={{ color: red[colorWeight] }}
                      />
                    </Tooltip>
                  );
                }

                return null;
              })()}
              {(() => {
                if (queues === undefined) {
                  return null;
                }

                if (lastDndStatus === "TakeAllCalls") {
                  if (
                    queues.find(
                      ({ acceptCurrentQueueCalls }) => !acceptCurrentQueueCalls
                    )
                  ) {
                    if (lastPresenceStatus === "Available") {
                      return (
                        <Tooltip
                          title={queues
                            .filter((queue) => !queue.acceptCurrentQueueCalls)
                            .map((queue) => (
                              <div className="notranslate" key={queue.queueId}>
                                {queue.name}
                              </div>
                            ))}
                          placeholder="Not Accepting"
                        >
                          <PersonOutline
                            aria-label="Signed out of queue"
                            aria-hidden={false}
                            role="img"
                            style={{ color: green[colorWeight] }}
                          />
                        </Tooltip>
                      );
                    }
                    return (
                      <Tooltip
                        title={queues
                          .filter((queue) => !queue.acceptCurrentQueueCalls)
                          .map((queue) => (
                            <div className="notranslate" key={queue.queueId}>
                              {queue.name}
                            </div>
                          ))}
                        placeholder="Busy"
                      >
                        <PersonOutline
                          aria-label="Not accepting queue calls"
                          aria-hidden={false}
                          role="img"
                          style={{ color: red[colorWeight] }}
                        />
                      </Tooltip>
                    );
                  }
                }

                return null;
              })()}
              {(() => {
                if (queues === undefined) {
                  return null;
                }

                if (lastDndStatus === "TakeAllCalls") {
                  if (
                    queues.find(
                      ({ acceptCurrentQueueCalls }) => acceptCurrentQueueCalls
                    )
                  ) {
                    if (lastPresenceStatus === "Available") {
                      return (
                        <Tooltip
                          title={queues
                            .filter((queue) => queue.acceptCurrentQueueCalls)
                            .map((queue) => (
                              <div className="notranslate" key={queue.queueId}>
                                {queue.name}
                              </div>
                            ))}
                          placeholder="Accepting"
                        >
                          <PersonIcon
                            aria-label="Accepting queue calls"
                            aria-hidden={false}
                            role="img"
                            style={{ color: green[colorWeight] }}
                          />
                        </Tooltip>
                      );
                    }
                    return (
                      <Tooltip
                        title={queues
                          .filter((queue) => queue.acceptCurrentQueueCalls)
                          .map((queue) => (
                            <div key={queue.queueId}>{queue.name}</div>
                          ))}
                        placeholder="Busy"
                        className="notranslate"
                      >
                        <PersonIcon
                          aria-label="Busy but accepting queue calls"
                          aria-hidden={false}
                          role="img"
                          style={{ color: red[colorWeight] }}
                        />
                      </Tooltip>
                    );
                  }
                }

                return null;
              })()}
            </div>
          </Typography>
        </CardContent>
      </>
    );

    return (
      <div
        className="user-card-container "
        style={{
          backgroundColor: "rgb(20, 20, 21)",
          borderRadius: 9,
          outline: (() => {
            if (isCurrentUser) {
              if(finalLate){
                return `3px solid ${borderColors.blue}`;
              }
              if (lastDndStatus === "DoNotAcceptAnyCalls") {
                return `3px solid ${borderColors.red}`;
              }

              if (lastPresenceStatus === "Offline") {
                return `3px solid ${borderColors.grey}`;
              }

              if (lastPresenceStatus === "Busy") {
                return `3px solid ${borderColors.red}`;
              }

              return `3px solid ${borderColors.green}`;
            }
            return "";
          })(),
          outlineOffset: (() => {
            if (isCurrentUser) {
              return "-3px";
            }
            return "";
          })(),
        }}
      >
        <Card
          className="user-card-outer"
          classes={{ root: classes.root }}
          style={{
            borderRadius: 8,
            animationDelay: timingFormat,
            backgroundColor: (() => {
              if(finalLate) {
                return blue[backgroundWeight];
              }
              if (lastDndStatus === "DoNotAcceptAnyCalls") {
                return red[backgroundWeight];
              }

              if (lastPresenceStatus === "Offline") {
                return grey[backgroundWeight];
              }

              if (lastPresenceStatus === "Busy") {
                return red[backgroundWeight];
              }

              return green[backgroundWeight];
            })(),
            color,
            display: "flex",
            alignItems: "center",
          }}
        >
          {isAdmin &&
            glipStatus !== "Disabled" &&
            (type === "User" || type === "DigitalUser") && (
              <CardActionArea
                role="button"
                onClick={() => adminClick(id)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                }}
                className="user-card-button"
              >
                {cardContent}
              </CardActionArea>
            )}
          {(!isAdmin ||
            glipStatus === "Disabled" ||
            (type !== "User" && type !== "DigitalUser")) &&
            cardContent}
        </Card>
      </div>
    );
  }
);
UserWidget.displayName = 'UserWidget';
export default UserWidget;
